/**
 * The intro teaser: a full-screen animated title scene that shows on page load
 * and *stays up* (its scene keeps animating — torches flicker, the noria turns,
 * the creature blinks) until the player chooses how to begin. It layers above
 * the (already rendered) intro overlay.
 *
 * The scene itself is per-game markup/CSS; this controller only wires the two
 * action buttons and the fade-out.
 *
 * Every game's index.html follows the same markup contract
 * (shared styles in @jpj/ui/components.css):
 *
 *   #teaser        .teaser         fixed full-screen scene, above all overlays
 *     .teaser-art                  the animated scene layers (pointer-events:none)
 *     .teaser-card                 kicker / title / sub text block …
 *       .teaser-actions            … and the two action buttons:
 *         #teaserHowTo  .btn.ghost   → reveals the intro / how-to-play overlay
 *         #teaserStart  .btn         → jumps straight into the first level
 *
 * On a button click the controller fades the teaser out (`.out`) and removes it
 * from the DOM (so it can never swallow another event). "Start" runs `onStart`
 * *before* the fade — while the teaser is still opaque — so the game is set up
 * and the intro overlay hidden behind the cover, and the fade reveals the
 * running game (never a flash of the how-to-play overlay). "How to Play" runs
 * after the fade and needs no callback: the intro overlay is shown by default
 * underneath, so removing the teaser reveals it. `prefers-reduced-motion` users
 * still get the title screen and its buttons — only the motion is dropped
 * (handled in CSS).
 */
export interface TeaserOptions {
  rootId?: string;
  howToId?: string;
  startId?: string;
  /** Length of the fade-out (keep in sync with the `.teaser.out` transition). */
  fadeMs?: number;
  /** Run after the teaser fades out from the "How to Play" button. */
  onHowTo?: () => void;
  /** Run from the "Start" button — before the fade — to begin the game. */
  onStart?: () => void;
}

export function runTeaser(opts: TeaserOptions = {}): void {
  const root = document.getElementById(opts.rootId ?? "teaser");
  if (!root) {
    opts.onStart?.();
    return;
  }
  const fadeMs = opts.fadeMs ?? 500;

  let done = false;
  /**
   * `before` runs the callback *while the teaser is still opaque*, then fades
   * out — used by "Start" so the game is set up (and the intro overlay hidden)
   * behind the cover, and the fade reveals the running game rather than a flash
   * of the how-to-play overlay. "How to Play" runs its callback after the fade,
   * deliberately revealing the intro overlay underneath.
   */
  const dismiss = (cb: (() => void) | undefined, before: boolean): void => {
    if (done) return;
    done = true;
    if (before) cb?.();
    root.classList.add("out");
    window.setTimeout(() => {
      root.remove();
      if (!before) cb?.();
    }, fadeMs);
  };

  document
    .getElementById(opts.howToId ?? "teaserHowTo")
    ?.addEventListener("click", () => dismiss(opts.onHowTo, false));
  document
    .getElementById(opts.startId ?? "teaserStart")
    ?.addEventListener("click", () => dismiss(opts.onStart, true));

  // gate the scene/entrance animations (idle loops keep running while up)
  root.classList.add("play");
}
